import { create } from 'zustand'

const mockDocuments = [
  {
    id: 'DOC-001',
    title: 'Getting Started Guide',
    category: 'guides',
    content: 'Welcome to Cybertection! This guide will help you get started...',
    author: 'Admin',
    createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    updatedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
    views: 342,
    helpful: 89,
    unhelpful: 5,
  },
  {
    id: 'DOC-002',
    title: 'Password Reset Instructions',
    category: 'faq',
    content: 'To reset your password, follow these steps...',
    author: 'Support Team',
    createdAt: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000),
    updatedAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000),
    views: 1205,
    helpful: 234,
    unhelpful: 12,
  },
  {
    id: 'DOC-003',
    title: 'Billing FAQ',
    category: 'faq',
    content: 'Common questions about billing and subscriptions...',
    author: 'Billing Team',
    createdAt: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000),
    updatedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
    views: 856,
    helpful: 145,
    unhelpful: 8,
  },
]

export const useKnowledgeStore = create((set, get) => ({
  documents: mockDocuments,
  searchQuery: '',
  selectedCategory: 'all',
  loading: false,

  // Fetch all documents
  fetchDocuments: async () => {
    set({ loading: true })
    try {
      // Integration point: Replace with actual API call
      // const response = await apiClient.get('/api/v1/knowledge/documents')
      await new Promise(resolve => setTimeout(resolve, 500))
      set({ loading: false })
    } catch (error) {
      set({ loading: false })
      throw error
    }
  },

  // Search documents
  searchDocuments: (query) => {
    set({ searchQuery: query })
    const { documents, selectedCategory } = get()

    if (!query && selectedCategory === 'all') {
      return documents
    }

    return documents.filter(doc => {
      const matchesQuery =
        !query ||
        doc.title.toLowerCase().includes(query.toLowerCase()) ||
        doc.content.toLowerCase().includes(query.toLowerCase())
      const matchesCategory = selectedCategory === 'all' || doc.category === selectedCategory
      return matchesQuery && matchesCategory
    })
  },

  // Set category filter
  setCategory: (category) => {
    set({ selectedCategory: category })
  },

  // Get document by ID
  getDocument: (docId) => {
    const { documents } = get()
    return documents.find(d => d.id === docId)
  },

  // Update document
  updateDocument: async (docId, updates) => {
    set({ loading: true })
    try {
      // Integration point: Replace with actual API call
      // await apiClient.patch(`/api/v1/knowledge/documents/${docId}`, updates)
      set((state) => ({
        documents: state.documents.map(d =>
          d.id === docId
            ? { ...d, ...updates, updatedAt: new Date() }
            : d
        ),
        loading: false,
      }))
    } catch (error) {
      set({ loading: false })
      throw error
    }
  },

  // Create new document
  createDocument: async (data) => {
    set({ loading: true })
    try {
      // Integration point: Replace with actual API call
      // const response = await apiClient.post('/api/v1/knowledge/documents', data)
      const newDocument = {
        id: `DOC-${String(get().documents.length + 1).padStart(3, '0')}`,
        ...data,
        createdAt: new Date(),
        updatedAt: new Date(),
        views: 0,
        helpful: 0,
        unhelpful: 0,
      }
      set((state) => ({
        documents: [newDocument, ...state.documents],
        loading: false,
      }))
      return newDocument
    } catch (error) {
      set({ loading: false })
      throw error
    }
  },

  // Delete document
  deleteDocument: async (docId) => {
    set({ loading: true })
    try {
      // Integration point: Replace with actual API call
      // await apiClient.delete(`/api/v1/knowledge/documents/${docId}`)
      set((state) => ({
        documents: state.documents.filter(d => d.id !== docId),
        loading: false,
      }))
    } catch (error) {
      set({ loading: false })
      throw error
    }
  },

  // Mark document as helpful
  markHelpful: async (docId) => {
    const { documents } = get()
    const doc = documents.find(d => d.id === docId)
    if (doc) {
      await get().updateDocument(docId, {
        helpful: doc.helpful + 1,
      })
    }
  },

  // Mark document as unhelpful
  markUnhelpful: async (docId) => {
    const { documents } = get()
    const doc = documents.find(d => d.id === docId)
    if (doc) {
      await get().updateDocument(docId, {
        unhelpful: doc.unhelpful + 1,
      })
    }
  },

  // Get categories
  getCategories: () => {
    const categories = new Set(get().documents.map(d => d.category))
    return Array.from(categories)
  },

  // Get popular documents
  getPopularDocuments: (limit = 5) => {
    return get().documents.sort((a, b) => b.views - a.views).slice(0, limit)
  },

  // Get statistics
  getStats: () => {
    const { documents } = get()
    return {
      totalDocuments: documents.length,
      categories: new Set(documents.map(d => d.category)).size,
      totalViews: documents.reduce((sum, d) => sum + d.views, 0),
      totalHelpful: documents.reduce((sum, d) => sum + d.helpful, 0),
    }
  },
}))
